import {useCallback} from 'react';
import {useDispatch} from 'react-redux';

import isEmpty from 'lodash.isempty';
import {useCachedTable} from 'src/hooks/common-data-service/cached-table';
import {getEntitlementList} from 'src/modules/entitlements/utils/getEntitlementList';
import {getProductAccountEntitlementRelations} from 'src/openapi/admin/apis/ProductAccountEntitlementsApi';
import {getProductGrantsByIds} from 'src/openapi/admin/apis/ProductEntitlementsGrantApi';
import {getProductResourcesByIds} from 'src/openapi/admin/apis/ProductEntitlementsResourceApi';
import {getProductRolesByIds} from 'src/openapi/admin/apis/ProductEntitlementsRoleApi';
import {useAppDispatch} from 'src/store/hooks';

import {mcpAccountEntitlementsStateSelector} from '../slices/mcp-account-entitlements-state.slice';
import {
    mcpAccountEntitlementsActions,
    mcpAccountEntitlementsDataSelector,
    mcpAccountEntitlementsResultSelector,
} from '../slices/mcp-account-entitlements.slice';


import type {CacheKeys} from 'src/hooks/common-data-service/cached-table';
import type {EntitlementsFilters} from 'src/modules/entitlements/typedef';
import type {EntitlementResource} from 'src/openapi/admin/models/EntitlementResource';
import type {Grant} from 'src/openapi/admin/models/Grant';
import type {RelationPage} from 'src/openapi/admin/models/RelationPage';
import type {Role} from 'src/openapi/admin/models/Role';

type GetProductAccountEntitlementsCacheKeys = CacheKeys<undefined, undefined, EntitlementsFilters>;

type Props = {
    accountId: number,
};

export const useGetProductAccountEntitlements = ({accountId}: Props) => {
    const apiDispatch = useAppDispatch();
    const dispatch = useDispatch();

    const getAction = useCallback((cacheKeys: GetProductAccountEntitlementsCacheKeys) => apiDispatch(
        getProductAccountEntitlementRelations({
            accountId,
            pageNumber: cacheKeys.pagination.currentPage,
            pageSize: cacheKeys.pagination.pageSize,
            sortDirection: cacheKeys.sortCriteria.direction,
            requestBody: cacheKeys.filters?.resourceIds as (string[] | undefined),
        }),
    ), [accountId, apiDispatch]);

    const setLoadedData = useCallback(async (
        response: RelationPage,
        cacheKeys: GetProductAccountEntitlementsCacheKeys,
    ) => {
        const filteredResources = cacheKeys.filters.resourceIds as string[] | undefined;
        const relations = filteredResources && !filteredResources.length
            ? [] : response.itemList || [];

        let roles: Record<string, Role> = {};
        const roleIds = [...new Set(relations
            .filter(({roleId}) => roleId)
            .map(({roleId}) => roleId || ''),
        )];
        const rolesResponse = await apiDispatch(getProductRolesByIds({requestBody: roleIds}));
        if (rolesResponse.payload.success) {
            roles = rolesResponse.payload.data;
        } else {
            dispatch(mcpAccountEntitlementsActions.onFailure(rolesResponse.payload.data));
            return;
        }

        let grants: Record<string, Grant> = {};
        const grantIds = [...new Set(relations
            .map(({grantId}) => grantId),
        )];
        const grantsResponse = await apiDispatch(getProductGrantsByIds({requestBody: grantIds}));
        if (grantsResponse.payload.success) {
            grants = grantsResponse.payload.data;
        } else {
            dispatch(mcpAccountEntitlementsActions.onFailure(grantsResponse.payload.data));
            return;
        }

        let resources: Record<string, EntitlementResource> = {};
        const resourceIds = [...new Set(relations
            .map(({resourceId}) => resourceId),
        )];
        const resourcesResponse = await apiDispatch(
            getProductResourcesByIds({requestBody: resourceIds}),
        );
        if (resourcesResponse.payload.success) {
            resources = resourcesResponse.payload.data;
        } else {
            dispatch(mcpAccountEntitlementsActions.onFailure(resourcesResponse.payload.data));
            return;
        }

        const data = relations.length && !isEmpty(grants) && !isEmpty(resources)
            ? getEntitlementList({
                    roles,
                    grants,
                    resources,
                    relations,
                    sortDirection: cacheKeys.sortCriteria.direction,
                }) : [];

        dispatch(mcpAccountEntitlementsActions.onSuccess({
            cacheKeys,
            totalCount: response.totalCount,
            data,
        }));
    }, [apiDispatch, dispatch]);

    return useCachedTable({
        dataSelector: mcpAccountEntitlementsDataSelector,
        stateSelector: mcpAccountEntitlementsStateSelector,
        resultSelector: mcpAccountEntitlementsResultSelector,
        actions: mcpAccountEntitlementsActions,
        getAction,
        onSuccessLoad: setLoadedData,
    });
};
